import React, { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as httpService from "../services/httpService";
import { Employee_Api_EndPoints } from "../global/end-points";
import LoadingSpinner from "../components/common/loadingSpinner";
import NoDoughCardMessage from "../components/common/nodoughcard_message";
import useActivityLogger from "../utils/useActivityLogger";

function FastCardBalance() {
  const didMount = useRef(false);
  const navigate = useNavigate();

  const [isLoad, setIsLoad] = useState(false);
  const [hasCard, setHasCard] = useState(null); // null = checking, true = has card, false = no card
  const [currentBalance, setCurrentBalance] = useState(0);
  const [cardLast4, setCardLast4] = useState("");

  const [isException, setIsException] = useState(false);
  const [exceptionMsg, setExceptionMsg] = useState({
    title: "Error",
    content: "An error occurred while fetching card balance.",
  });

  // ✅ OBO detection
  const effectiveUserId =
    sessionStorage.getItem("OnBehalfUserId") || sessionStorage.getItem("UserId");

  // If this exists => LoginOnBehalf mode
  const isOBO = !!sessionStorage.getItem("OnBehalfUserId");

  const { logActivity, logApiCall } = useActivityLogger({
    moduleCode: "DPY",
  });

  useEffect(() => {
    logActivity({
      activity: "PAGE_VIEW",
      description: `${isOBO ? "OBO-" : ""}FastCardBalance Page View`,
      component: `${isOBO ? "OBO." : ""}Page.FastCardBalance`,
    });

    if (!didMount.current) {
      checkCardStatus();
    }

    return () => {
      didMount.current = true;
    };
    // eslint-disable-next-line
  }, []);

  const checkCardStatus = () => {
    if (!effectiveUserId) {
      // No user ID - navigate to Get Card Instructions
      // Pass state to indicate this came from Card button (no back button needed)
      navigate("/getcardinstructions", { state: { fromCardButton: true } });
      return;
    }

    // ✅ Safety timeout to ensure loading state is always set (prevents infinite loading)
    const timeoutId = setTimeout(() => {
      console.warn("Card status check timeout - setting error state");
      setIsException(true);
      setExceptionMsg({
        title: "Error",
        content: "Request timed out. Please try again.",
      });
      setIsLoad(true);
      setHasCard(false);
    }, 15000); // 15 second timeout

    const body = { UserId: Number(effectiveUserId) };

    httpService.Get(Employee_Api_EndPoints.GET_USER_INFO, body)
      .then((resp) => {
        try {
          if (resp && typeof resp === 'object' && resp.status === 200 && resp.data) {
            const phone =
              resp.data.PhoneNumber || resp.data.phoneNumber || resp.data.phone;

            if (!phone) {
              // ✅ Clear timeout before navigation
              clearTimeout(timeoutId);
              // No phone number - navigate to Get Card Instructions
              // Pass state to indicate this came from Card button (no back button needed)
              navigate("/getcardinstructions", { state: { fromCardButton: true } });
              return;
            }

            // ✅ FIXED: Use CallWithHeaders directly from service
            httpService
              .CallWithHeaders(
                "get",
                Employee_Api_EndPoints.CARD_ACCOUNT_STATUS,
                { phoneNumber: phone },
                { SecretKey: "DoUgHPaYSeCrEKeY" }
              )
              .then((resp2) => {
                try {
                  if (resp2 && typeof resp2 === 'object' && resp2.status === 200 && resp2.data) {
                    const userHasCard = !!resp2.data.hasCard;
                    sessionStorage.setItem("CardRegistrationStatus", userHasCard ? "registered" : "not_registered");
                    setHasCard(userHasCard);

                    if (!userHasCard) {
                      // ✅ Clear timeout before navigation
                      clearTimeout(timeoutId);
                      // Navigate to Get Card Instructions screen instead of showing error
                      // Pass state to indicate this came from Card button (no back button needed)
                      navigate("/getcardinstructions", { state: { fromCardButton: true } });
                      return;
                    }

                    const adminNum =
                      resp2.data.transcardAdminNumber ||
                      resp2.data.adminNumber ||
                      resp2.data.AdminNumber;

                    if (!adminNum) {
                      // ✅ Clear timeout before setting error
                      clearTimeout(timeoutId);
                      setIsException(true);
                      const onBehalfUserName = sessionStorage.getItem("OnBehalfUserName");
                      setExceptionMsg({
                        title: "Error",
                        content: isOBO && onBehalfUserName
                          ? `Unable to fetch card balance for ${onBehalfUserName}. Transcard Admin Number not found.`
                          : "Transcard Admin Number not found. Unable to fetch card balance.",
                      });
                      setIsLoad(true);
                      setHasCard(false);
                      return;
                    }

                    // ✅ Clear timeout before making final API call
                    clearTimeout(timeoutId);
                    getFastCardBalance(adminNum);
                  } else {
                    // ✅ Clear timeout before navigation
                    clearTimeout(timeoutId);
                    // No card data - navigate to Get Card Instructions
                    navigate("/getcardinstructions", { state: { fromCardButton: true } });
                    return;
                  }
                } catch (error) {
                  // ✅ Clear timeout before navigation
                  clearTimeout(timeoutId);
                  console.error("Error processing card account status:", error);
                  // On error, navigate to Get Card Instructions
                  navigate("/getcardinstructions", { state: { fromCardButton: true } });
                  return;
                }
              })
              .catch((error) => {
                // ✅ Clear timeout before navigation
                clearTimeout(timeoutId);
                console.error("Error fetching card account status:", error);
                // On error, navigate to Get Card Instructions
                navigate("/getcardinstructions", { state: { fromCardButton: true } });
                return;
              });
          } else {
            // ✅ Clear timeout before navigation
            clearTimeout(timeoutId);
            // No phone number - navigate to Get Card Instructions
            navigate("/getcardinstructions", { state: { fromCardButton: true } });
            return;
          }
        } catch (error) {
          // ✅ Clear timeout before navigation
          clearTimeout(timeoutId);
          console.error("Error processing user info:", error);
          // On error, navigate to Get Card Instructions
          navigate("/getcardinstructions", { state: { fromCardButton: true } });
          return;
        }
      })
      .catch((error) => {
        // ✅ Clear timeout before navigation
        clearTimeout(timeoutId);
        console.error("Error fetching user info:", error);
        // On error, navigate to Get Card Instructions
        navigate("/getcardinstructions", { state: { fromCardButton: true } });
        return;
      });
  };

  const getFastCardBalance = (adminNum) => {
    const body = { AdminNumber: Number(adminNum) };

    logApiCall(
      `${isOBO ? "OBO." : ""}API.FastCardBalance`,
      `${isOBO ? "OBO-" : ""}FastCardBalance (request)`
    );

    // ✅ Safety timeout for balance API call
    const balanceTimeoutId = setTimeout(() => {
      console.warn("Card balance API timeout - setting error state");
      setIsException(true);
      setExceptionMsg({
        title: "Error",
        content: "Request timed out while fetching card balance. Please try again.",
      });
      setIsLoad(true);
      setHasCard(false);
    }, 10000); // 10 second timeout for balance call

    httpService.Post(Employee_Api_EndPoints.FAST_CARD_BALANCE, body)
      .then((resp) => {
        try {
          if (resp && typeof resp === 'object' && resp.status === 200 && resp.data) {
            const balance =
              resp.data?.CurrentBalance ??
              resp.data?.currentBalance ??
              resp.data?.balance ??
              0;

            setCurrentBalance(Number(balance) || 0);
            
            // Try to get card last 4 digits if available
            const last4 = resp.data?.cardLast4Digits || 
                         resp.data?.cardLast4 || 
                         resp.data?.CardLast4Digits ||
                         resp.data?.CardLast4 ||
                         "";
            if (last4) {
              setCardLast4(String(last4).slice(-4));
            }
            
            // ✅ Clear timeout and set loading complete
            clearTimeout(balanceTimeoutId);
            setIsLoad(true);
          } else {
            // ✅ Clear timeout before setting error
            clearTimeout(balanceTimeoutId);
            setIsException(true);
            const onBehalfUserName = sessionStorage.getItem("OnBehalfUserName");
            setExceptionMsg({
              title: "Error",
              content: isOBO && onBehalfUserName
                ? `Failed to retrieve card balance for ${onBehalfUserName}.`
                : "Failed to retrieve card balance.",
            });
            setIsLoad(true);
            setHasCard(false);
          }
        } catch (error) {
          // ✅ Clear timeout before setting error
          clearTimeout(balanceTimeoutId);
          setIsException(true);
          const onBehalfUserName = sessionStorage.getItem("OnBehalfUserName");
          setExceptionMsg({
            title: "Error",
            content: isOBO && onBehalfUserName
              ? `Unable to retrieve card balance for ${onBehalfUserName}. Please try again.`
              : "Unable to retrieve card balance. Please try again.",
          });
          setIsLoad(true);
          setHasCard(false);
        }
      },
      (err) => {
        // ✅ Clear timeout before setting error
        clearTimeout(balanceTimeoutId);
        setIsException(true);
        const onBehalfUserName = sessionStorage.getItem("OnBehalfUserName");

        if (err && err.response && err.response.status === 401) {
          const msg =
            err.response?.data?.message ||
            err.response?.data?.title ||
            "Unauthorized";
          setExceptionMsg({ title: "Error", content: msg });
        } else {
          setExceptionMsg({
            title: "Error",
            content: isOBO && onBehalfUserName
              ? `Unable to retrieve card balance for ${onBehalfUserName}. Please try again.`
              : "Unable to retrieve card balance. Please try again.",
          });
        }

        setIsLoad(true);
        setHasCard(false);
      }
    ).catch(() => {
      // ✅ Clear timeout before setting error
      clearTimeout(balanceTimeoutId);
      setIsException(true);
      const onBehalfUserName = sessionStorage.getItem("OnBehalfUserName");
      setExceptionMsg({
        title: "Error",
        content: isOBO && onBehalfUserName
          ? `Unable to retrieve card balance for ${onBehalfUserName}. Please try again.`
          : "Unable to retrieve card balance. Please try again.",
      });
      setIsLoad(true);
      setHasCard(false);
    });
  };


  // ✅ masked text in OBO
  const balanceText = isOBO ? "$ XXXX" : `$${Number(currentBalance || 0).toFixed(2)}`;

  // ✅ Handle Back button click
  const handleBackClick = (e) => {
    e.preventDefault();
    logActivity({
      activity: "NAVIGATION",
      description: `${isOBO ? "OBO-" : ""}Clicked Back from Card Balance`,
      component: `${isOBO ? "OBO.Button.Back" : "DPY.Button.Back"}`,
    });
    navigate("/home"); // Navigate to home screen
  };

  return (
    <Fragment>
      {/* ✅ Show loader until ALL data is loaded (hasCard set AND balance loaded) */}
      {!isLoad && <LoadingSpinner />}
      
      {isLoad && hasCard === true && !isException && (
        <div className="dashboard-container">
          {/* ✅ Back button positioned on the left corner */}
          <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-start', marginBottom: '15px', paddingLeft: '0' }}>
            <a href="#" className="back-link" onClick={handleBackClick}>
              <span className="back-arrow-icon"></span>
              <span>Back</span>
            </a>
          </div>

          <h2 className="page-module-heading mb-15"> Card Balance</h2>

          <div className="fast-card-balance-wrapper">
            <div className="realistic-card-container">
              <div className="realistic-card">
                <div className="realistic-card-background"></div>
                <div className="realistic-card-content">
                  <div className="realistic-card-top">
                    <div className="realistic-card-chip">
                      <div className="chip-lines">
                        <div className="chip-line"></div>
                        <div className="chip-line"></div>
                        <div className="chip-line"></div>
                        <div className="chip-line"></div>
                        <div className="chip-line"></div>
                        <div className="chip-line"></div>
                      </div>
                    </div>
                    <div className="realistic-card-provider">
                      <div className="contactless-icon">
                        <svg viewBox="0 0 24 24" fill="none">
                          <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                  
                  <div className="realistic-card-number">
                    {cardLast4 ? (
                      <>
                        <span className="card-number-masked">**** **** ****</span>
                        <span className="card-number-visible">{cardLast4}</span>
                      </>
                    ) : (
                      <span className="card-number-masked">**** **** **** ****</span>
                    )}
                  </div>
                  
                  <div className="realistic-card-balance-section">
                    <div className="realistic-card-balance-label">Current Balance</div>
                    <div className="realistic-card-balance-amount">{balanceText}</div>
                  </div>
                  
                  <div className="realistic-card-bottom">
                    <div className="realistic-card-holder-section">
                      <div className="realistic-card-label">Cardholder</div>
                      <div className="realistic-card-holder-name">
                        {sessionStorage?.UserName || "CARD HOLDER"}
                      </div>
                    </div>
                    <div className="realistic-card-expiry-section">
                      <div className="realistic-card-label">Expires</div>
                      <div className="realistic-card-expiry">••/••</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isException && (
        <NoDoughCardMessage
          title={exceptionMsg.title}
          message={exceptionMsg.content}
          onClose={() => setIsException(false)}
        />
      )}
    </Fragment>
  );
}

export default FastCardBalance;
